import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, Container, Typography, Paper, TextField, Button, Avatar, 
  Grid2 as Grid, IconButton, Fade, Alert, InputAdornment, Snackbar
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon, Delete as DeleteIcon, 
  Visibility, VisibilityOff, Save as SaveIcon 
} from '@mui/icons-material';
import { getMe, updateProfile } from '../api/api';

const getAvatarUrl = (avatar) => {
  if (!avatar) return "";
  const avatarStr = avatar.toString();
  if (avatarStr.startsWith("http")) return avatarStr;
  const isPredefined = /^[1-6]$/.test(avatarStr);
  if (isPredefined) {
    return `https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-chat/ava${avatarStr}-bg.webp`;
  }
  const baseUrl = process.env.REACT_APP_CHAT_BASE_URL || "http://localhost:5001";
  return `${baseUrl}/uploads/${avatarStr}`;
};

function Settings() {
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [avatarUrl, setAvatarUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await getMe();
        setUser(currentUser);
        setFirstName(currentUser.firstName);
        setLastName(currentUser.lastName);
        setAvatarUrl(currentUser.avatar);
      } catch (err) {
        setError('Failed to load profile settings');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size should be less than 5MB");
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      const formData = new FormData();
      if (firstName !== user.firstName) formData.append('firstName', firstName);
      if (lastName !== user.lastName) formData.append('lastName', lastName);
      
      if (currentPassword || newPassword) {
        if (!currentPassword) throw new Error('Please enter your current password to set a new one');
        if (!newPassword || newPassword.length < 6) throw new Error('New password must be at least 6 characters');
        formData.append('currentPassword', currentPassword);
        formData.append('newPassword', newPassword);
      }

      if (selectedFile) {
        formData.append('image', selectedFile);
      }

      const res = await updateProfile(formData);
      
      setSuccess('Profile updated successfully!');
      setUser(res.user);
      localStorage.setItem('user', JSON.stringify(res.user));
      setAvatarUrl(res.user.avatar);
      clearSelectedFile();
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const textFieldStyle = {
    color: '#111827',
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: 'rgba(0,0,0,0.1)',
      borderRadius: '16px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#111827',
      boxShadow: '0 0 15px rgba(0,0,0,0.05)'
    },
    '& .MuiInputBase-input': {
      padding: '16px 20px',
      fontFamily: "'Inter', sans-serif"
    }
  };

  const hasChanges = (
    firstName !== user?.firstName ||
    lastName !== user?.lastName ||
    selectedFile !== null ||
    currentPassword.length > 0 ||
    newPassword.length > 0
  );

  return (
    <Box sx={{ 
      minHeight: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
      background: "transparent", py: 4, fontFamily: "'Inter', sans-serif" 
    }}>
      <Container maxWidth="sm" sx={{ zIndex: 50, pb: 10 }}>
        <Paper elevation={0} className="fade-in" sx={{
          p: { xs: 3, md: 5 }, borderRadius: '24px', background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(24px)", boxShadow: "0 16px 40px -10px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.04)", color: "#111827"
        }}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h4" sx={{ 
              fontFamily: "'Anton', sans-serif", fontWeight: 400, mb: 1, letterSpacing: "-0.02em", color: "#111827", textTransform: 'uppercase'
            }}>
              SETTINGS
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#4B5563', fontFamily: "'Inter', sans-serif" }}>
              Update your profile information and password
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Box sx={{ position: "relative", mb: 2 }}>
              <Avatar 
                src={previewUrl || getAvatarUrl(avatarUrl)} 
                sx={{ width: 100, height: 100, border: "4px solid #111827", boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }} 
              />
              {previewUrl && (
                <IconButton 
                  size="small" 
                  onClick={clearSelectedFile} 
                  sx={{ position: "absolute", top: -8, right: -8, bgcolor: "#EF4444", color: "white", "&:hover": { bgcolor: "#DC2626" } }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}
              sx={{ 
                borderRadius: 3, textTransform: "none", py: 1, px: 2, fontWeight: 600, fontFamily: "'Inter', sans-serif",
                borderColor: "rgba(0,0,0,0.2)", color: "#111827",
                "&:hover": { borderColor: "#111827", bgcolor: "rgba(0,0,0,0.02)" }
              }}
            >
              Change Photo
              <input type="file" hidden accept="image/*" ref={fileInputRef} onChange={handleFileChange} />
            </Button>
          </Box>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                InputProps={{ sx: textFieldStyle }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1, fontFamily: "'Inter', sans-serif" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)}
                InputProps={{ sx: textFieldStyle }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1, fontFamily: "'Inter', sans-serif" } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700, color: "#111827", fontFamily: "'Inter', sans-serif" }}>
                 Change Password
               </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Current Password" type={showCurrentPassword ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                InputProps={{ 
                  endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" sx={{ color: '#6B7280' }}>{showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                  sx: textFieldStyle 
                }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1, fontFamily: "'Inter', sans-serif" } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="New Password" type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{ 
                  endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: '#6B7280' }}>{showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                  sx: textFieldStyle 
                }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1, fontFamily: "'Inter', sans-serif" } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
              <Button fullWidth variant="contained" onClick={handleSave} disabled={saving || !hasChanges}
                startIcon={<SaveIcon />}
                sx={{
                  py: 2, fontSize: "1rem", fontWeight: 700, borderRadius: 4, textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif", background: "#111827", color: "white",
                  boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
                  "&:hover": { background: "#000", transform: "translateY(-2px)" },
                  "&:disabled": { color: "rgba(0,0,0,0.3)", background: "rgba(0,0,0,0.05)" }
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Toaster Messages */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success" variant="filled" sx={{ width: '100%', borderRadius: 2, fontFamily: "'Inter', sans-serif" }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%', borderRadius: 2, fontFamily: "'Inter', sans-serif" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
